import { MANIFEST, type Zone, type ZoneId } from '../analytics/interaction-manifest'

/**
 * Per-route head config (spec §3.2). One entry per prerendered route, derived from the
 * interaction manifest so routes exist in exactly one place. Copy constraints (enforced
 * by seo.config.test.ts): title <= 60 chars, description 150-160 chars.
 *
 * Descriptions are adapted from the live CONTENT copy for each region — no invented
 * capabilities (data/content.ts is the source narrative).
 */

export const ORIGIN = 'https://shinro.dev'
export const THEME_COLOR = '#070A08' // --canvas-bg (dark default, styles/tokens.css)
// --canvas-bg from the [data-theme="light"] block. Emitted as a prefers-color-scheme
// variant so the browser chrome matches the page a light-preference visitor actually
// gets. Best-effort by nature: a localStorage override is only known at runtime, and
// a static meta cannot express it.
export const THEME_COLOR_LIGHT = '#FAFAF9'

export interface RouteHead {
  route: string
  title: string
  description: string
  canonical: string
  ogImage: string
  /** zone backing this route, when it is a canvas zone route */
  zoneId?: ZoneId
  /** static page (no app boot): prerendered content is the final page */
  staticPage?: boolean
  /**
   * emit <meta name="robots" content="noindex"> and keep out of sitemap/llms.txt.
   * No entry here sets it — the only noindex page is /404, which scripts/prerender.mjs
   * builds outside SEO_ROUTES precisely so it stays out of the sitemap.
   */
  noindex?: boolean
}

const ZONE_HEADS: Record<ZoneId, { title: string; description: string }> = {
  home: {
    title: 'Shinro — The development environment for robotics',
    description:
      'Describe your robot, wire its behavior from modules. Compile it to a native binary for your hardware. Run ROS as a module for deeper debugging metrics.',
  },
  pain: {
    title: 'The Problem — Shinro | Stop rebuilding robotics plumbing',
    description:
      'Every robotics team rebuilds drivers, deploy pipelines, debug tooling, and hardware abstraction. Shinro ends the non-differentiating infrastructure work.',
  },
  timing: {
    title: 'Why Now — Shinro | Heterogeneous hardware, AI behavior',
    description:
      'Hardware is heterogeneous now — Jetson, ESP32, custom boards — and AI is the application layer. Shinro is the development environment built for the shift.',
  },
  positioning: {
    title: 'The Foundation — Shinro | Build once, run on anything',
    description:
      'Shinro sits between robotics frameworks and hardware targets: one consistent way to compose, build, deploy, and observe robot software across runtimes.',
  },
  product: {
    title: 'Studio — Shinro | Describe, compose, simulate, deploy',
    description:
      'One environment: describe the machine, compose behavior visually, simulate with real-time physics, deploy native binaries, then swap modules live at runtime.',
  },
  architecture: {
    title: 'The Architecture — Shinro | Composed at compile time',
    description:
      'Most frameworks compose components at runtime over a message bus. Shinro composes at compile time: the toolchain generates one native binary on the cake kernel.',
  },
  workflow: {
    title: 'The Developer Loop — Shinro | Develop, build, observe',
    description:
      'Compose in Shinro Studio, cross-compile with the cake toolchain, deploy or hot-swap modules at runtime, and watch telemetry stream back. Then iterate again.',
  },
  'platform-model': {
    title: 'Modules — Shinro | Studio, community modules, and cake',
    description:
      'Three components: Shinro Studio for visual development, community-published modules ready to drop into your build, and cake — the cross-platform kernel.',
  },
  differentiation: {
    title: 'Live Modules — Shinro | Hot-swap modules, no restart',
    description:
      'A digital twin of a robot arm picks and places while you drag its block away. Mount a gripper, camera, or force control while it runs — no restart needed.',
  },
  licensing: {
    title: 'Licensing — Shinro | Fair-code Studio, open modules',
    description:
      "Shinro Studio is source in the open under fair-code terms, free to use. Modules carry their authors' licenses. The cake kernel is commercial, distributed as-is.",
  },
  action: {
    title: 'Come Build — Shinro | Robot software that stays live',
    description:
      'Come build robot software that stays live. Try Shinro Studio, read the docs, and find the team on GitHub — or talk to the team directly for early access.',
  },
}

function ogSlug(route: string): string {
  return route === '/' ? 'home' : route.slice(1).replace(/\//g, '-')
}

function headForZone(zone: Zone): RouteHead {
  const copy = ZONE_HEADS[zone.id]
  return {
    route: zone.route,
    title: copy.title,
    description: copy.description,
    canonical: ORIGIN + zone.route,
    ogImage: `/og/${ogSlug(zone.route)}.png`,
    zoneId: zone.id,
  }
}

/** Static (non-zone) prerendered pages. */
const STATIC_PAGES: RouteHead[] = [
  {
    route: '/privacy',
    title: 'Privacy — Shinro | Analytics, consent, and your choices',
    description:
      'What shinro.dev collects and why: consent-gated Google Analytics 4 and PostHog EU with session replay, what we never collect, and how to change your choice.',
    canonical: `${ORIGIN}/privacy`,
    ogImage: '/og/privacy.png',
    staticPage: true,
  },
  {
    route: '/faq',
    title: 'FAQ — Shinro | Licensing, ROS, hardware, and installs',
    description:
      'Answers about Shinro: what it is, fair-code licensing, running ROS as a module, simulating before deploying, supported hardware from Jetson down to ESP32.',
    canonical: `${ORIGIN}/faq`,
    ogImage: '/og/faq.png',
    staticPage: true,
  },
]

/** All prerendered routes: the 11 zone routes + static pages. */
export const SEO_ROUTES: RouteHead[] = [...MANIFEST.zones.map(headForZone), ...STATIC_PAGES]

export function headForRoute(route: string): RouteHead | null {
  const path = route !== '/' && route.endsWith('/') ? route.slice(0, -1) : route
  return SEO_ROUTES.find((r) => r.route === path) ?? null
}

/** True when the path is a prerendered static page that must NOT boot the app. */
export function isStaticPage(pathname: string): boolean {
  return headForRoute(pathname)?.staticPage === true
}
